import { useEffect, useState, type ReactNode } from 'react';
import {
  Crown,
  Prohibit,
  SealCheck,
  SpeakerHigh,
  SpeakerSlash,
  Trash,
  Warning,
} from '@phosphor-icons/react';
import type { PublicUser } from '@sirocco/shared';
import { moderation } from '../../api/endpoints';
import type {
  ActionModeration,
  DossierDuMembre,
  RoleModeration,
} from '../../firebase/moderation';
import { TextArea } from '../../components/ui/Field';
import { ConfirmDialog, useConfirm } from '../../components/ui/Confirm';
import { useAsync } from '../../hooks/useAsync';
import { locale, t } from '../../i18n';

/**
 * Les gestes d'administration sur un membre, d'ou qu'on les fasse.
 *
 * L'ecran d'administration les prend par le pseudo ; le profil d'un
 * membre les offre a l'administrateur sans detour, sous la fiche. Les
 * deux partagent tout : le dossier, la liste des gestes, et le formulaire
 * qui exige le motif.
 *
 * Rien ne s'ecrit ici. Chaque geste part vers `agirEnModeration`, qui
 * verifie le role, exige un motif, refuse a un moderateur les trois
 * gestes reserves a l'administrateur, et journalise. Les boutons caches
 * ne protegent rien : ils evitent seulement de proposer ce qui sera
 * refuse.
 *
 * # Ce que chaque geste veut dire
 *
 * **Avertir** est le seul qui parle : le membre le lit a sa prochaine
 * ouverture et doit en accuser reception.
 *
 * **Reduire au silence** retire la parole publique — les posts et les
 * reponses — et rien d'autre : il lit, il ecrit a ses amis, il rejoint
 * des sorties. Avec un terme de preference : un silence dont on ignore la
 * fin est une suspension qui n'ose pas dire son nom.
 *
 * **Suspendre** ferme le compte. **Supprimer** l'efface, avec tout ce
 * qu'il a ecrit, et rien ne le rend.
 */

/** Les durees proposees, et ce qu'elles valent en millisecondes. */
const DUREES: { label: string; ms: number | null }[] = [
  { label: t.moderation.admin.durees.jour, ms: 24 * 3600 * 1000 },
  { label: t.moderation.admin.durees.semaine, ms: 7 * 24 * 3600 * 1000 },
  { label: t.moderation.admin.durees.mois, ms: 30 * 24 * 3600 * 1000 },
  { label: t.moderation.admin.durees.sansTerme, ms: null },
];

/** Ce qu'un geste demande en plus du motif. */
interface Geste {
  action: ActionModeration;
  titre: string;
  /** Le texte que le membre lira. `avertir` seulement. */
  avecTexte?: boolean;
  /** Le choix d'une duree. `museler` seulement. */
  avecDuree?: boolean;
  /** Le role a donner. `nommer` seulement. */
  role?: RoleModeration | null;
  /** La coche. `verifier` seulement. */
  actif?: boolean;
  /** Ce qu'on demande de confirmer avant d'envoyer. */
  confirmation?: string;
}

const ICONES: Partial<Record<ActionModeration, ReactNode>> = {
  avertir: <Warning size={20} />,
  museler: <SpeakerSlash size={20} />,
  liberer: <SpeakerHigh size={20} />,
  suspendre: <Prohibit size={20} />,
  retablir: <Prohibit size={20} />,
  verifier: <SealCheck size={20} />,
  nommer: <Crown size={20} />,
  supprimer: <Trash size={20} />,
};

function quand(ms: number): string {
  return new Date(ms).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
}

/** Mon role, lu une fois et partage par tous les ecrans qui en dependent. */
export function useMonRole(): RoleModeration | null {
  const { data } = useAsync(() => moderation.myRole(), [], 'mon-role');
  return data ?? null;
}

/** Ce qui pese sur le membre, en une ligne : c'est ce qu'on lit avant d'agir. */
export function etatDuDossier(dossier: DossierDuMembre): string {
  const etat = t.moderation.admin.etat;
  return (
    [
      dossier.role === 'admin'
        ? etat.administrateur
        : dossier.role === 'moderateur'
          ? etat.moderateur
          : null,
      dossier.verifie ? etat.verifie : null,
      dossier.suspendu ? etat.suspendu : null,
      dossier.muet
        ? dossier.muet.jusqua
          ? etat.muetJusqua(quand(dossier.muet.jusqua))
          : etat.muet
        : null,
      dossier.signalements ? etat.signalements(dossier.signalements) : null,
      dossier.avertissements.length ? etat.avertissements(dossier.avertissements.length) : null,
    ]
      .filter(Boolean)
      .join(' · ') || t.moderation.admin.rien
  );
}

/** Les gestes possibles sur ce membre, dans l'ordre de gravite. */
function listeDesGestes(d: DossierDuMembre, admin: boolean): Geste[] {
  const gestes = t.moderation.admin.gestes;
  const liste: Geste[] = [
    { action: 'avertir', titre: gestes.avertir, avecTexte: true },
    d.muet
      ? { action: 'liberer', titre: gestes.liberer }
      : { action: 'museler', titre: gestes.museler, avecDuree: true },
    d.suspendu
      ? { action: 'retablir', titre: gestes.retablir }
      : { action: 'suspendre', titre: gestes.suspendre, confirmation: gestes.suspendreQ },
  ];

  if (!admin) return liste;

  return [
    ...liste,
    d.verifie
      ? { action: 'verifier', titre: gestes.retirerCoche, actif: false }
      : { action: 'verifier', titre: gestes.poserCoche, actif: true },
    ...(d.role === null
      ? [
          { action: 'nommer' as const, titre: gestes.nommerModerateur, role: 'moderateur' as const },
          { action: 'nommer' as const, titre: gestes.nommerAdmin, role: 'admin' as const },
        ]
      : [{ action: 'nommer' as const, titre: gestes.retirerRole, role: null }]),
    { action: 'supprimer', titre: gestes.supprimer, confirmation: gestes.supprimerQ },
  ];
}

export interface OutilsDAdministration {
  dossier: DossierDuMembre | null;
  gestes: Geste[];
  choisir: (geste: Geste) => void;
  /** Ce que le dernier geste a donne. */
  dit: string | null;
  /**
   * La question et le formulaire du geste.
   *
   * A poser dans la fente `overlay` de l'ecran : `.confirm-root` se place
   * par rapport a son conteneur, et dans la zone qui defile il s'ouvrait
   * sous le pli des qu'on avait descendu.
   */
  fenetre: ReactNode;
}

/**
 * Tout ce qu'il faut pour agir sur un membre.
 *
 * `membre` nul : rien ne se lit, et il n'y a rien a montrer. C'est ainsi
 * qu'un ecran qui ne s'adresse pas qu'aux administrateurs l'appelle
 * quand meme, les hooks ne se posant pas sous condition.
 */
export function useGestesDuMembre(
  membre: PublicUser | null,
  {
    apresGeste,
    apresSuppression,
  }: {
    /** Apres un geste reussi, pour relire ce que l'ecran montre du membre. */
    apresGeste?: () => void;
    /** Le compte n'existe plus : l'ecran n'a plus rien a montrer de lui. */
    apresSuppression: () => void;
  },
): OutilsDAdministration {
  const role = useMonRole();
  const admin = role === 'admin';
  const id = membre?.id ?? null;

  const [dossier, setDossier] = useState<DossierDuMembre | null>(null);
  const [geste, setGeste] = useState<Geste | null>(null);
  const [motif, setMotif] = useState('');
  const [texte, setTexte] = useState('');
  const [duree, setDuree] = useState<number | null>(DUREES[1].ms);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [dit, setDit] = useState<string | null>(null);

  const { pending, request, confirm, cancel } = useConfirm();

  /* Le dossier ne se lit que pour qui a un role : les autres n'y ont pas acces. */
  useEffect(() => {
    setDossier(null);
    setDit(null);
    if (!id || !role) return;
    let vivant = true;
    void moderation
      .dossier(id)
      .then((lu) => vivant && setDossier(lu))
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, [id, role]);

  function proposer(choisi: Geste) {
    setGeste(choisi);
    setMotif('');
    setTexte('');
    setDuree(DUREES[1].ms);
    setErreur(null);
  }

  function choisir(choisi: Geste) {
    if (choisi.confirmation) request(choisi.confirmation, () => proposer(choisi));
    else proposer(choisi);
  }

  async function envoyer() {
    if (!geste || !membre) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await moderation.act(geste.action, motif, undefined, membre.id, {
        ...(geste.avecTexte ? { texte } : {}),
        ...(geste.avecDuree ? { jusqua: duree === null ? null : Date.now() + duree } : {}),
        ...(geste.action === 'nommer' ? { role: geste.role ?? null } : {}),
        ...(geste.action === 'verifier' ? { actif: geste.actif } : {}),
      });
      setGeste(null);
      /* Le compte efface n'a plus de dossier a relire. */
      if (geste.action === 'supprimer') {
        apresSuppression();
        return;
      }
      setDit(t.moderation.admin.fait(geste.titre));
      setDossier(await moderation.dossier(membre.id).catch(() => null));
      apresGeste?.();
    } catch (cause) {
      setErreur(cause instanceof Error ? cause.message : t.moderation.admin.echec);
    } finally {
      setEnvoi(false);
    }
  }

  /*
    Le formulaire du geste. Il ne s'ouvre qu'une fois le geste choisi,
    et il porte toujours le motif : c'est ce qui restera dans le journal,
    et c'est la seule chose qu'on relira dans six mois.
  */
  const fenetre = (
    <>
      <ConfirmDialog pending={pending} onCancel={cancel} onConfirm={confirm} />

      {geste && membre && (
        <div className="confirm-root">
          <button
            className="confirm__scrim"
            aria-label={t.commun.annuler}
            onClick={() => setGeste(null)}
          />
          <div className="confirm confirm--avertissement" role="dialog" aria-modal="true">
            <p className="confirm__titre">
              {geste.titre} · {membre.username}
            </p>

            {geste.avecTexte && (
              <TextArea
                label={t.moderation.admin.texteMembre}
                rows={4}
                maxLength={1000}
                value={texte}
                onChange={(e) => setTexte(e.target.value)}
              />
            )}

            {geste.avecDuree && (
              <div className="adm__durees">
                {DUREES.map((d) => (
                  <button
                    key={d.label}
                    className={`adm__duree${duree === d.ms ? ' adm__duree--on' : ''}`}
                    onClick={() => setDuree(d.ms)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            )}

            <TextArea
              label={t.moderation.admin.pourquoi}
              rows={2}
              maxLength={300}
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />

            {erreur && <p className="adm__erreur">{erreur}</p>}

            <div className="confirm__actions">
              <button className="confirm__no" onClick={() => setGeste(null)}>
                {t.commun.annuler}
              </button>
              <button
                className="confirm__yes"
                disabled={envoi || !motif.trim() || (geste.avecTexte && !texte.trim())}
                onClick={() => void envoyer()}
              >
                {envoi ? t.commun.unInstant : t.commun.confirmer}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return {
    dossier,
    gestes: dossier ? listeDesGestes(dossier, admin) : [],
    choisir,
    dit,
    fenetre,
  };
}

/**
 * La colonne de gestes, et l'histoire des avertissements.
 *
 * Un seul geste par ligne, pleine largeur, et le seul irreversible en
 * rouge. `avecEtat` ajoute la ligne d'etat en tete : l'ecran
 * d'administration la pose deja a cote du visage.
 */
export function GestesDuMembre({
  outils,
  avecEtat = false,
}: {
  outils: OutilsDAdministration;
  avecEtat?: boolean;
}) {
  const { dossier, gestes, choisir, dit } = outils;
  if (!dossier) return null;

  return (
    <>
      {avecEtat && <p className="adm__etat">{etatDuDossier(dossier)}</p>}

      {dit && <p className="adm__dit">{dit}</p>}

      <div className="adm__gestes">
        {gestes.map((g) => (
          <button
            key={`${g.action}-${g.titre}`}
            className={`adm__geste${g.action === 'supprimer' ? ' adm__geste--danger' : ''}`}
            onClick={() => choisir(g)}
          >
            <span className="adm__geste-icone">{ICONES[g.action]}</span>
            {g.titre}
          </button>
        ))}
      </div>

      {dossier.avertissements.length > 0 && (
        <div className="adm__histoire">
          <p className="adm__titre">{t.moderation.admin.avertissements}</p>
          {dossier.avertissements.map((a) => (
            <p key={a.at} className="adm__ligne-histoire">
              {quand(a.at)} · {a.lu ? t.moderation.admin.etat.lu : t.moderation.admin.etat.pasLu} ·{' '}
              {a.texte}
            </p>
          ))}
        </div>
      )}
    </>
  );
}
